import { ActionIcon, Button, Card, Group, MultiSelect, Stack, Table, Text, Title } from "@mantine/core";
import { IconUserMinus, IconUserPlus } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import useSWR from "swr";
import { TenantFeePolicyAssignmentApi } from "../../apis";
import type { FeePolicyCustomer } from "../../apis/tenantFeePolicyAssignment";
import { Message } from "../../utils/message";
import { PermissionGate } from "./PermissionGate";

interface Props {
  /** NAMED policy in ACTIVE/SCHEDULED status — the only states the backend allows assignments for. */
  policyId: string;
}

const typeLabel = (type: string): string => {
  switch (type) {
    case "BUYER":
      return "Худалдан авагч";
    case "SUPPLIER":
      return "Нийлүүлэгч";
    default:
      return type || "—";
  }
};

// Backend bulk endpoint caps customerBusinessIds at 100 per call.
const BULK_CHUNK = 100;

/**
 * "Оноосон харилцагчид" section of a NAMED fee-policy detail page: lists the
 * customers currently on this policy, lets the admin add more via a searchable
 * picker (bulk assign) and revert individual customers to DEFAULT (unassign).
 */
export function PolicyCustomersSection({ policyId }: Props) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  // Customers currently assigned to this policy.
  const { data: assignedData, mutate: mutateAssigned } = useSWR(
    ["swr.my.fee-policy-customers.assigned", policyId] as const,
    () => TenantFeePolicyAssignmentApi.listCustomers({ feePolicyId: policyId, limit: 1000 })
  );
  // Full roster for the picker — anyone not already on this policy is offerable.
  const { data: rosterData, mutate: mutateRoster } = useSWR(
    "swr.my.fee-policy-customers.roster-all",
    () => TenantFeePolicyAssignmentApi.listCustomers({ limit: 1000 })
  );

  const assigned: FeePolicyCustomer[] = assignedData?.rows ?? [];

  const options = useMemo(() => {
    const rows: FeePolicyCustomer[] = rosterData?.rows ?? [];
    return rows
      .filter((r) => r.effectivePolicy?.id !== policyId)
      .map((r) => ({
        value: r.businessId,
        label:
          r.effectivePolicy && r.effectivePolicy.scope === "NAMED"
            ? `${r.name ?? r.businessId} — ${r.effectivePolicy.name ?? "(нэргүй)"}`
            : `${r.name ?? r.businessId}`
      }));
  }, [rosterData, policyId]);

  const refresh = async () => {
    await Promise.all([mutateAssigned(), mutateRoster()]);
  };

  const handleAssign = async () => {
    if (selectedIds.length === 0) return;
    setBusy(true);
    try {
      for (let i = 0; i < selectedIds.length; i += BULK_CHUNK) {
        await TenantFeePolicyAssignmentApi.assignBulk({
          feePolicyId: policyId,
          customerBusinessIds: selectedIds.slice(i, i + BULK_CHUNK)
        });
      }
      setSelectedIds([]);
      await refresh();
      Message.success("Харилцагч амжилттай оноогдлоо.");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      Message.error(e?.message ?? "Алдаа гарлаа");
    } finally {
      setBusy(false);
    }
  };

  const handleUnassign = async (row: FeePolicyCustomer) => {
    if (!row.assignmentId) return;
    setBusy(true);
    try {
      await TenantFeePolicyAssignmentApi.unassign(row.assignmentId);
      await refresh();
      Message.success("DEFAULT бодлогод буцаагдлаа.");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      Message.error(e?.message ?? "Алдаа гарлаа");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card withBorder padding="md">
      <Stack gap="sm">
        <Title order={5}>Оноосон харилцагчид ({assignedData?.total ?? 0})</Title>

        <PermissionGate code="BIL_016">
          <Group align="flex-end" gap="sm" wrap="wrap">
            <MultiSelect
              label="Харилцагч нэмэх"
              placeholder={options.length === 0 ? "Оноох боломжтой харилцагч алга" : "Харилцагч сонгох"}
              data={options}
              value={selectedIds}
              onChange={setSelectedIds}
              searchable
              clearable
              hidePickedOptions
              maxDropdownHeight={280}
              w={420}
              disabled={busy || options.length === 0}
            />
            <Button
              leftSection={<IconUserPlus size={16} />}
              onClick={handleAssign}
              loading={busy}
              disabled={selectedIds.length === 0}
            >
              Оноох
            </Button>
          </Group>
        </PermissionGate>

        <Table verticalSpacing="xs" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Харилцагч</Table.Th>
              <Table.Th>Төрөл</Table.Th>
              <Table.Th style={{ width: 60 }}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {assigned.map((c) => (
              <Table.Tr key={c.businessId}>
                <Table.Td><Text size="sm" fw={600}>{c.name ?? c.businessId}</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">{typeLabel(c.type)}</Text></Table.Td>
                <Table.Td>
                  <PermissionGate code="BIL_015">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => handleUnassign(c)}
                      disabled={busy || !c.assignmentId}
                      aria-label="DEFAULT-руу буцаах"
                      title="DEFAULT-руу буцаах"
                    >
                      <IconUserMinus size={16} />
                    </ActionIcon>
                  </PermissionGate>
                </Table.Td>
              </Table.Tr>
            ))}
            {assigned.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={3}>
                  <Text c="dimmed" ta="center" py="sm">
                    Одоогоор оноосон харилцагч алга. Дээрх талбараас сонгож онооно уу.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  );
}
